import { useState } from 'react'
import { useApp } from '../context/AppContext'
import { persistAppendOnlyVersion, persistDeletedVersion, persistDeletedVersions } from '../lib/lumiAppendOnly'
import { gerarCodigo, nowISO } from '../utils'
import type { Unidade, Obra, CentroCusto } from '../types'

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="modal-panel" style={{ width: 480, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 className="modal-panel-title" style={{ fontSize: 15, fontWeight: 600 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#62666d', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

function SBadge({ status }: { status: string }) {
  const m: Record<string, string> = { EM_ANDAMENTO: '#34d399', CONCLUIDA: '#60a5fa', PARALISADA: '#fbbf24', CANCELADA: '#f87171', ATIVO: '#34d399', INATIVO: '#9ca3af' }
  const c = m[status] || '#9ca3af'
  return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: c + '1a', color: c }}>{status.replace(/_/g, ' ')}</span>
}

export default function Obras() {
  const { unidades, setUnidades, obras, setObras, centrosCusto, setCentrosCusto, contratos, addLog } = useApp()
  const [tab, setTab] = useState<'unidades' | 'obras' | 'centros'>('obras')
  const [modal, setModal] = useState<'unidade' | 'obra' | 'centro' | null>(null)
  const [editId, setEditId] = useState<string | null>(null)
  const [editDbId, setEditDbId] = useState<string | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [erro, setErro] = useState('')
  const [toast, setToast] = useState('')
  const [saving, setSaving] = useState(false)

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000) }
  function openNew(t: typeof modal) { setForm({}); setEditId(null); setEditDbId(null); setErro(''); setModal(t) }
  function field(k: string, v: string) { setForm(p => ({ ...p, [k]: v })) }

  const inp = (label: string, key: string, type = 'text', disabled = false) => {
    const fieldId = `obras-${modal ?? 'form'}-${key}`
    return (
      <div style={{ marginBottom: 12 }}>
        <label className="label-field" htmlFor={fieldId}>{label}</label>
        <input id={fieldId} className="input-field" type={type} value={form[key] || ''} onChange={e => field(key, e.target.value)} disabled={disabled} style={disabled ? { opacity: 0.5 } : {}} />
      </div>
    )
  }
  const sel = (label: string, key: string, options: { value: string; label: string }[]) => {
    const fieldId = `obras-${modal ?? 'form'}-${key}`
    return (
      <div style={{ marginBottom: 12 }}>
      <label className="label-field" htmlFor={fieldId}>{label}</label>
      <select id={fieldId} className="input-field" value={form[key] || ''} onChange={e => field(key, e.target.value)}>
        <option value="">Selecione...</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
    )
  }

  async function saveUnidade() {
    if (!form.nome?.trim()) { setErro('Nome é obrigatório.'); return }
    setSaving(true)
    try {
      if (editId && editDbId) {
        const unidadeAtual = unidades.find(u => u.id === editId || (u as any)._id === editDbId)
        const atualizada = { ...unidadeAtual, nome: form.nome, descricao: form.descricao, status: (form.status || 'ATIVO') as Unidade['status'], id: editId, updatedAt: nowISO() } as Unidade
        await persistAppendOnlyVersion('unidades', atualizada as unknown as Record<string, unknown>)
        setUnidades(p => p.map(u => u.id === editId || (u as any)._id === editDbId ? atualizada : u))
        addLog('UNIDADE', editId, 'EDITADO', `Unidade ${form.nome} editada`)
        showToast('Unidade atualizada.')
      } else {
        const novo: Unidade = { id: 'u' + Date.now(), codigo: gerarCodigo(), nome: form.nome, descricao: form.descricao, status: 'ATIVA', createdAt: nowISO() }
        await persistAppendOnlyVersion('unidades', novo as unknown as Record<string, unknown>)
        setUnidades(p => [...p, novo])
        addLog('UNIDADE', novo.id, 'CRIADO', `Unidade ${novo.nome} cadastrada`)
        showToast('Unidade cadastrada.')
      }
      setModal(null)
    } catch { setErro('Erro ao salvar. Tente novamente.') } finally { setSaving(false) }
  }

  async function saveObra() {
    if (!form.contrato_id) { setErro('Selecione o contrato.'); return }
    if (!form.unidade_id) { setErro('Selecione a unidade.'); return }
    if (!form.nome_obra?.trim()) { setErro('Nome da obra é obrigatório.'); return }
    setSaving(true)
    try {
      if (editId && editDbId) {
        const obraAtual = obras.find(o => o.id === editId || (o as any)._id === editDbId)
        const atualizada = { ...obraAtual, ...form, id: editId, updatedAt: nowISO() } as Obra
        await persistAppendOnlyVersion('obras', atualizada as unknown as Record<string, unknown>)
        setObras(p => p.map(o => o.id === editId || (o as any)._id === editDbId ? atualizada : o))
        addLog('OBRA', editId, 'EDITADO', `Obra ${form.nome_obra} editada`)
        showToast('Obra atualizada.')
      } else {
        const novo: Omit<Obra, '_id'> = { id: 'o' + Date.now(), contrato_id: form.contrato_id, unidade_id: form.unidade_id, codigo_obra: gerarCodigo(), nome_obra: form.nome_obra, descricao: form.descricao, cidade: form.cidade, estado: form.estado, data_inicio_prevista: form.data_inicio_prevista, data_fim_prevista: form.data_fim_prevista, status: 'EM_ANDAMENTO', gerente_responsavel: form.gerente_responsavel, createdAt: nowISO() }
        await persistAppendOnlyVersion('obras', novo as unknown as Record<string, unknown>)
        setObras(p => [...p, novo])
        addLog('OBRA', novo.id, 'CRIADO', `Obra ${novo.nome_obra} cadastrada`)
        showToast('Obra cadastrada.')
      }
      setModal(null)
    } catch { setErro('Erro ao salvar. Tente novamente.') } finally { setSaving(false) }
  }

  async function saveCentro() {
    if (!form.obra_id) { setErro('Selecione a obra.'); return }
    if (!form.nome?.trim()) { setErro('Nome é obrigatório.'); return }
    setSaving(true)
    try {
      if (editId && editDbId) {
        const centroAtual = centrosCusto.find(c => c.id === editId || (c as any)._id === editDbId)
        const atualizado = { ...centroAtual, nome: form.nome, descricao: form.descricao, tipo: form.tipo, status: (form.status || 'ATIVO') as CentroCusto['status'], id: editId, updatedAt: nowISO() } as CentroCusto
        await persistAppendOnlyVersion('centros_de_custo', atualizado as unknown as Record<string, unknown>)
        setCentrosCusto(p => p.map(c => c.id === editId || (c as any)._id === editDbId ? atualizado : c))
        addLog('CENTRO_CUSTO', editId, 'EDITADO', 'Centro de custo editado')
        showToast('Centro atualizado.')
      } else {
        const novo: Omit<CentroCusto, '_id'> = { id: 'cc' + Date.now(), obra_id: form.obra_id, codigo: gerarCodigo(), nome: form.nome, descricao: form.descricao, tipo: form.tipo, status: 'ATIVO', createdAt: nowISO() }
        await persistAppendOnlyVersion('centros_de_custo', novo as unknown as Record<string, unknown>)
        setCentrosCusto(p => [...p, novo])
        addLog('CENTRO_CUSTO', novo.id, 'CRIADO', `Centro de custo ${novo.nome} cadastrado`)
        showToast('Centro cadastrado.')
      }
      setModal(null)
    } catch { setErro('Erro ao salvar. Tente novamente.') } finally { setSaving(false) }
  }

  async function deleteUnidade(unidade: Unidade) {
    const obrasVinculadas = obras.filter(o => o.unidade_id === unidade.id || o.unidade_id === (unidade as any)._id)
    const obraIds = new Set(obrasVinculadas.flatMap(o => [o.id, (o as any)._id].filter(Boolean)))
    const centrosVinculados = centrosCusto.filter(c => obraIds.has(c.obra_id))
    const confirmed = window.confirm(`Excluir a unidade ${unidade.nome}? Tambem serao excluidas ${obrasVinculadas.length} obra(s) e ${centrosVinculados.length} centro(s) vinculado(s).`)
    if (!confirmed) return

    setSaving(true)
    try {
      await persistDeletedVersions('centros_de_custo', centrosVinculados as unknown as Record<string, unknown>[])
      await persistDeletedVersions('obras', obrasVinculadas as unknown as Record<string, unknown>[])
      await persistDeletedVersion('unidades', unidade as unknown as Record<string, unknown>)
      setCentrosCusto(prev => prev.filter(c => !centrosVinculados.some(v => v.id === c.id || (v as any)._id === (c as any)._id)))
      setObras(prev => prev.filter(o => !obrasVinculadas.some(v => v.id === o.id || (v as any)._id === (o as any)._id)))
      setUnidades(prev => prev.filter(u => u.id !== unidade.id && (u as any)._id !== (unidade as any)._id))
      addLog('UNIDADE', unidade.id, 'CANCELADO', `Unidade ${unidade.nome} excluida com obras e centros vinculados`)
      showToast('Unidade excluida.')
    } catch {
      setErro('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteObra(obra: Obra) {
    const centrosVinculados = centrosCusto.filter(c => c.obra_id === obra.id || c.obra_id === (obra as any)._id)
    const confirmed = window.confirm(`Excluir a obra ${obra.nome_obra}? Tambem serao excluidos ${centrosVinculados.length} centro(s) vinculado(s).`)
    if (!confirmed) return

    setSaving(true)
    try {
      await persistDeletedVersions('centros_de_custo', centrosVinculados as unknown as Record<string, unknown>[])
      await persistDeletedVersion('obras', obra as unknown as Record<string, unknown>)
      setCentrosCusto(prev => prev.filter(c => !centrosVinculados.some(v => v.id === c.id || (v as any)._id === (c as any)._id)))
      setObras(prev => prev.filter(o => o.id !== obra.id && (o as any)._id !== (obra as any)._id))
      addLog('OBRA', obra.id, 'CANCELADO', `Obra ${obra.nome_obra} excluida com ${centrosVinculados.length} centro(s) vinculado(s)`)
      showToast('Obra excluida.')
    } catch {
      setErro('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteCentro(centro: CentroCusto) {
    const confirmed = window.confirm(`Excluir o centro de custo ${centro.nome}? Esta acao nao pode ser desfeita.`)
    if (!confirmed) return

    setSaving(true)
    try {
      await persistDeletedVersion('centros_de_custo', centro as unknown as Record<string, unknown>)
      setCentrosCusto(prev => prev.filter(c => c.id !== centro.id && (c as any)._id !== (centro as any)._id))
      addLog('CENTRO_CUSTO', centro.id, 'CANCELADO', `Centro de custo ${centro.nome} excluido`)
      showToast('Centro de custo excluido.')
    } catch {
      setErro('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  const ESTADOS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 20, background: '#1a2e1a', border: '1px solid #34d399', color: '#34d399', padding: '10px 16px', borderRadius: 8, zIndex: 100, fontSize: 13 }}>{toast}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600 }}>Obras e Centros</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={() => openNew('unidade')}>+ Unidade</button>
          <button className="btn-ghost" onClick={() => openNew('centro')}>+ Centro de Custo</button>
          <button className="btn-primary" onClick={() => openNew('obra')}>+ Obra</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {(['obras', 'unidades', 'centros'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 500 : 400, background: tab === t ? 'rgba(240,64,59,0.2)' : 'rgba(255,255,255,0.04)', color: tab === t ? '#ffb4b1' : '#8a8f98' }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'obras' && (
        <div className="card">
          {obras.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#62666d', fontSize: 13 }}>Nenhuma obra. <button className="btn-ghost" style={{ marginLeft: 8 }} onClick={() => openNew('obra')}>+ Obra</button></div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Código', 'Nome', 'Cidade/UF', 'Início Prev.', 'Gerente', 'Status', ''].map(h => <th key={h} style={{ color: '#62666d', fontSize: 11, padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>{h}</th>)}
              </tr></thead>
              <tbody>{obras.map(o => (
                <tr key={o._id ?? o.id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{o.codigo_obra}</td>
                  <td style={{ padding: '10px 16px', color: '#d0d6e0', fontSize: 13 }}>{o.nome_obra}</td>
                  <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{o.cidade}/{o.estado}</td>
                  <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{o.data_inicio_prevista}</td>
                  <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{o.gerente_responsavel}</td>
                  <td style={{ padding: '10px 16px' }}><SBadge status={o.status} /></td>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => { setForm({ ...o as unknown as Record<string, string> }); setEditId(o.id); setEditDbId(o._id ?? o.id); setErro(''); setModal('obra') }}>Editar</button>
                      <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12, color: '#f87171' }} onClick={() => deleteObra(o)} disabled={saving}>Excluir</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'unidades' && (
        <div className="card">
          {unidades.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#62666d', fontSize: 13 }}>Nenhuma unidade.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Código', 'Nome', 'Descrição', 'Status', ''].map(h => <th key={h} style={{ color: '#62666d', fontSize: 11, padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>{h}</th>)}
              </tr></thead>
              <tbody>{unidades.map(u => (
                <tr key={u._id ?? u.id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{u.codigo}</td>
                  <td style={{ padding: '10px 16px', color: '#d0d6e0', fontSize: 13 }}>{u.nome}</td>
                  <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 13 }}>{u.descricao}</td>
                  <td style={{ padding: '10px 16px' }}><SBadge status={u.status} /></td>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => { setForm({ ...u as unknown as Record<string, string> }); setEditId(u.id); setEditDbId(u._id ?? u.id); setErro(''); setModal('unidade') }}>Editar</button>
                      <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12, color: '#f87171' }} onClick={() => deleteUnidade(u)} disabled={saving}>Excluir</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'centros' && (
        <div className="card">
          {centrosCusto.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#62666d', fontSize: 13 }}>Nenhum centro de custo.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Código', 'Nome', 'Obra', 'Tipo', 'Status', ''].map(h => <th key={h} style={{ color: '#62666d', fontSize: 11, padding: '10px 16px', textAlign: 'left', fontWeight: 500 }}>{h}</th>)}
              </tr></thead>
              <tbody>{centrosCusto.map(c => {
                const ob = obras.find(o => o.id === c.obra_id || o._id === c.obra_id)
                return (
                  <tr key={c._id ?? c.id} className="table-row-hover" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{c.codigo}</td>
                    <td style={{ padding: '10px 16px', color: '#d0d6e0', fontSize: 13 }}>{c.nome}</td>
                    <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 13 }}>{ob?.nome_obra || '-'}</td>
                    <td style={{ padding: '10px 16px', color: '#8a8f98', fontSize: 12 }}>{c.tipo}</td>
                    <td style={{ padding: '10px 16px' }}><SBadge status={c.status} /></td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => { setForm({ ...c as unknown as Record<string, string> }); setEditId(c.id); setEditDbId(c._id ?? c.id); setErro(''); setModal('centro') }}>Editar</button>
                        <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12, color: '#f87171' }} onClick={() => deleteCentro(c)} disabled={saving}>Excluir</button>
                      </div>
                    </td>
                  </tr>
                )
              })}</tbody>
            </table>
          )}
        </div>
      )}

      {modal === 'unidade' && (
        <Modal title={editId ? 'Editar Unidade' : 'Nova Unidade'} onClose={() => setModal(null)}>
          {inp('Nome da Unidade *', 'nome')}
          {inp('Descrição', 'descricao')}
          {sel('Status', 'status', [{ value: 'ATIVO', label: 'Ativo' }, { value: 'INATIVO', label: 'Inativo' }])}
          {erro && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>⚠ {erro}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn-primary" onClick={saveUnidade} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </Modal>
      )}
      {modal === 'obra' && (
        <Modal title={editId ? 'Editar Obra' : 'Nova Obra'} onClose={() => setModal(null)}>
          <div style={{ background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.22)', borderRadius: 6, padding: '9px 11px', marginBottom: 12 }}>
            <p style={{ color: '#bfdbfe', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Codigo da obra</p>
            <p style={{ color: '#8a8f98', fontSize: 12, margin: 0 }}>
              O codigo e gerado automaticamente pelo sistema no momento do cadastro para garantir unicidade e rastreabilidade. O numero oficial da obra deve ser informado no nome ou descricao quando existir uma codificacao externa.
            </p>
          </div>
          {sel('Contrato *', 'contrato_id', contratos.map(c => ({ value: c._id ?? c.id, label: c.nome_contrato })))}
          {sel('Unidade *', 'unidade_id', unidades.map(u => ({ value: u._id ?? u.id, label: u.nome })))}
          {inp('Nome da Obra *', 'nome_obra')}
          {inp('Descrição', 'descricao')}
          {inp('Cidade', 'cidade')}
          {sel('Estado', 'estado', ESTADOS.map(e => ({ value: e, label: e })))}
          {inp('Início Previsto', 'data_inicio_prevista', 'date')}
          {inp('Fim Previsto', 'data_fim_prevista', 'date')}
          {inp('Gerente Responsável', 'gerente_responsavel')}
          {erro && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>⚠ {erro}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn-primary" onClick={saveObra} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </Modal>
      )}
      {modal === 'centro' && (
        <Modal title={editId ? 'Editar Centro de Custo' : 'Novo Centro de Custo'} onClose={() => setModal(null)}>
          {sel('Obra *', 'obra_id', obras.map(o => ({ value: o._id ?? o.id, label: o.nome_obra })))}
          {inp('Nome *', 'nome')}
          {inp('Descrição', 'descricao')}
          {inp('Tipo', 'tipo')}
          {erro && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>⚠ {erro}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn-ghost" onClick={() => setModal(null)}>Cancelar</button>
            <button className="btn-primary" onClick={saveCentro} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </Modal>
      )}
    </div>
  )
}
